import { observable, action, computed, makeObservable } from 'mobx';
import { Configuration } from 'nomst/Stores/Configuration/configuration';
import { Employee } from 'nomst/Stores/Decision/employee';
import { DecisionDispatcher } from 'nomst/Stores/Decision/decision.dispatcher';
import { CountryBudgetDecisionDispatcher } from 'nomst/Stores/Decision/countryBudgetDecision.dispatcher';
import { CurrencyConversion } from 'nomst/Stores/Utility/currencyConversion';
import { CountryBudget } from 'nomst/Stores/Decision/countryBudget';
import {
    ICountryBudgetDecision
} from 'typings/countryBudgetDecision';
import Coalesce from 'nomst/Stores/Utility/coalesce';
import { AmountCode, EmployeeNameFormat, EntityTypes } from 'typings/enums';
import { EmployeeSort } from 'nomst/Stores/Decision/Constants/employeeSort';
import { compareItem } from 'common/compareItem';
import { dataManagerFactory, managerListSidePanelStore, navigateToNoMst } from 'utils/MobxDataManager/singletons';
import { featureFlagHelper } from 'singletons';
import { FeatureFlags } from 'typings/enums';
import { IEmployee } from 'typings/employee';

export class CountryBudgetDecision implements ICountryBudgetDecision {

    @computed
    get employees(): Employee[] {
        if (this.searchedEmployee) {
            return [this.searchedEmployee];
        }

        const employees = Array.from(this._employeesMap.values())
            .filter(ee => !this.showPromotionsOnly || ee.hasPromotionRequest)
            .filter(ee => !this.filterByJob || ee.jobTitleInternal.toLowerCase() === this.filterByJob.toLowerCase())
            .filter(ee => !this.filterByDepartment || ee.department?.toLowerCase() === this.filterByDepartment?.toLowerCase());

        // Employees come back sorted from the backend
        // So only sort here if the user has changed what we're sorting by
        if (!this.isSortFormatUpdated) {
            return employees;
        }

        return employees.sort((employee: Employee, nextEmployee: Employee) => {
            switch (this.employeeSort) {
                case EmployeeSort.NameSortOption:
                    return compareItem(this.employeeNameForSort(employee), this.employeeNameForSort(nextEmployee));
                case EmployeeSort.TitleSortOption:
                    return compareItem(employee.jobTitleInternal, nextEmployee.jobTitleInternal);
                case EmployeeSort.HireDateSortOption:
                    return compareItem(employee.hireDate, nextEmployee.hireDate);
                case EmployeeSort.LastPayChangeDateSortOption:
                    return compareItem(employee.payEffectiveDate, nextEmployee.payEffectiveDate);
                case EmployeeSort.PerformanceRatingSortOption:
                    return compareItem(employee.performanceRating, nextEmployee.performanceRating);
                default:
                    return compareItem(
                        employee.convertedAnnualizedCurrentPay,
                        nextEmployee.convertedAnnualizedCurrentPay,
                        this.employeeSort === EmployeeSort.CurrentPayAscending);  
            }
        });
    }

    @computed
    get limitedEmployees(): Employee[] {
        return this.employees.slice(0, this._maxViewableEmployees);
    }

    @computed
    get employeesCount(): number {
        return Coalesce.array(this.employees).length;
    }

    @computed
    get budgets(): ICountryBudget[] {
        return Array.from(this._budgetsMap.values());
    }

    @computed
    get overBudgetCountryBudgets(): ICountryBudget[] {
        return this.budgets.filter(countryBudget => countryBudget.isOverBudget);
    }

    readonly countryBudgetDecisionDispatcher: CountryBudgetDecisionDispatcher;

    code: string;

    isoCurrencyCode: string;

    annualStandardWorkHours: number;

    @observable name: string;

    @observable employeeSort: EmployeeSort = EmployeeSort.NameSortOption;

    @observable showPromotionsOnly: boolean = false;

    @observable filterByJob: string = '';

    @observable filterByDepartment: string = '';

    @observable selectedEmployeeSourceId: Nullable<string>;

    @observable selectedConversationEntityId: Nullable<string>;

    @observable isUsingBudgetBar: boolean;

    @observable searchedEmployee: Nullable<Employee>;

    @observable isSortFormatUpdated: boolean = false;

    @observable page: number; 

    @observable count: number;

    @observable jobTitles: string[];

    @observable departments: string[];

    @observable private _maxViewableEmployees: number =
        featureFlagHelper?.featureFlagSettings[FeatureFlags.EmployeesPerPage] as number;

    country: string;

    isSorting: boolean;

    private _numberEmployeesPerPage: number = 
        featureFlagHelper?.featureFlagSettings[FeatureFlags.EmployeesPerPage] as number;

    private _decisionDispatcher: DecisionDispatcher;

    private readonly _budgetsMap: Map<number, ICountryBudget>;

    private readonly _budgetNamesMap: Map<number, string>;

    @observable private _employeesMap: Map<string, Employee>;

    private readonly _configuration: Configuration;

    private _conversationSummariesDtoMap: Map<string, IConversationSummary>;

    private readonly nameFormatMap: { [key: string]: (e: Employee) => string } = {
        [EmployeeNameFormat.FirstName]: (e: Employee) =>
            e.firstName ? e.firstName : e.displayName,
        [EmployeeNameFormat.FirstNameLastName]: (e: Employee) =>
            e.firstName ? `${e.firstName} ${e.lastName}` : e.displayName,
        [EmployeeNameFormat.FirstNameMiddleInitialLastName]: (e: Employee) => {
            const middleInitial = e.middleName ? e.middleName.charAt(0) : '';
            return e.firstName ? `${e.firstName} ${middleInitial} ${e.lastName}` : e.displayName;
        },
        [EmployeeNameFormat.LastNameFirstName]: (e: Employee) =>
            e.lastName ? `${e.lastName} ${e.firstName}` : e.displayName,
        [EmployeeNameFormat.LastNameFirstNameMiddleInitial]: (e: Employee) => {
            const middleInitial = e.middleName ? e.middleName.charAt(0) : '';
            return e.lastName ? `${e.lastName} ${middleInitial} ${e.firstName}` : e.displayName;
        }
    };

    constructor(
        countryBudgetDecisionDto: ICountryBudgetDecision,
        conversationSummariesDto: IConversationSummary[],
        configuration: Configuration,
        decisionDispatcher: DecisionDispatcher) {
        makeObservable(this);
        this._configuration = configuration;
        this.code = countryBudgetDecisionDto.code;
        this.isoCurrencyCode = countryBudgetDecisionDto.isoCurrencyCode;
        this.name = countryBudgetDecisionDto.name;
        this.countryBudgetDecisionDispatcher = new CountryBudgetDecisionDispatcher(this);
        this.page = countryBudgetDecisionDto.page;
        this.count = countryBudgetDecisionDto.count;
        this.country = countryBudgetDecisionDto.name;
        this._decisionDispatcher = decisionDispatcher;
        this.isSorting = false;
        this.showPromotionsOnly = countryBudgetDecisionDto.showPromotionsOnly;
        this.jobTitles = countryBudgetDecisionDto.jobTitles?.sort() ?? [];
        this.departments = countryBudgetDecisionDto.departments?.sort() ?? [];

        const employeesDto: Employee[] = Coalesce.array(countryBudgetDecisionDto.employees);
        this._conversationSummariesDtoMap = new Map<string, IConversationSummary>();
        conversationSummariesDto.forEach(conversationSummaryDto =>
            this._conversationSummariesDtoMap.set(conversationSummaryDto.entityId, conversationSummaryDto));

        this._employeesMap = new Map(employeesDto.map(employeeDto => {
            const conversationSummaryDto = this._conversationSummariesDtoMap.get(
                employeeDto.employeeSourceId) ?? null;

            return [
                employeeDto.employeeSourceId,
                new Employee(
                    employeeDto,
                    countryBudgetDecisionDto.annualStandardWorkHours ?? 0,
                    conversationSummaryDto,
                    configuration,
                    decisionDispatcher,
                    this.countryBudgetDecisionDispatcher)
            ] as [string, Employee];
        }));

        this._budgetsMap = new Map(
            Coalesce
                .array(countryBudgetDecisionDto.budgets)
                .map(countryBudget =>
                    [
                        countryBudget.budgetCycleId,
                        new CountryBudget(
                            countryBudget,
                            this.isoCurrencyCode,
                            new CurrencyConversion(configuration.currencies))
                    ] as [number, CountryBudget]));

        this._budgetNamesMap = new Map(
            Coalesce
                .array(countryBudgetDecisionDto.budgets)
                .map(countryBudget =>
                    [
                        countryBudget.budgetCycleId,
                        countryBudget.name] as [number, string]));
    }

    handleEmployeeSelection(employeeSourceId: string): Promise<void> {
        if (this.selectedEmployeeSourceId === employeeSourceId) {
            this.deselectEmployee();
            navigateToNoMst.navToUpdateEmployee(null);
            return Promise.resolve();
        }

        navigateToNoMst.navToUpdateEmployee(employeeSourceId);
        return this.selectEmployee(employeeSourceId);
    }

    removeEmployeeSelection(): void {
        if (!this.selectedEmployeeSourceId) {
            return;
        }
        this.deselectEmployee();
        navigateToNoMst.navToUpdateEmployee(null);
    }

    // tslint:disable:parameters-max-number
    handleAmountChanged(
        amountCode: AmountCode,
        amountIsoCurrencyCode: string,
        previousAmountValue: number,
        currentAmountValue: number,
        updateNewPay: boolean,
        isHourly: boolean,
        annualStandardWorkHours: number,
        fullTimeEquivalent: number): void {
        const cycleBudgetField = this._configuration.getCycleBudgetField(amountCode);
        if (!cycleBudgetField) {
            return;
        }

        const countryBudget = this.getBudget(cycleBudgetField.budgetCycleId);
        if (!countryBudget) {
            return;
        }

        if (amountCode === AmountCode.Stock) {
            countryBudget.addStockSpent(
                previousAmountValue, currentAmountValue
            );
        } else {
            countryBudget.addSpent(
                amountIsoCurrencyCode,
                previousAmountValue,
                currentAmountValue,
                updateNewPay,
                isHourly,
                annualStandardWorkHours,
                fullTimeEquivalent);
        }
    }

    handleAmountUpdated(
        employeeSourceId: string,
        amountCode: AmountCode): void {
        const employee = this.getEmployee(employeeSourceId);
        if (!employee) {
            return;
        }

        employee.handleAutoCalculateLumpSum(amountCode);
    }

    @action
    async increaseEmployeeViewSize(isDirectsOnly: boolean): Promise<void> {
        const dataManager = dataManagerFactory.getManagerDataManager();
        const employees = await dataManager.getPagedEmployees(
            this._configuration.masterAccountId,
            this._configuration.cycleId,
            managerListSidePanelStore.selectedManagerIds,
            isDirectsOnly,
            this.employeeSort,
            this._numberEmployeesPerPage,
            this.page + 1,
            this._configuration.teamPageViews.multiCountry ? this.code : '',
            1,
            this.showPromotionsOnly
        );
        await this.addPagedEmployeesToMap(employees);
        this.page ++;
        this._maxViewableEmployees += this._numberEmployeesPerPage;
    }

    @action
    async addPagedEmployeesToMap(employees: Nullable<IEmployee[]>): Promise<void> {
        if (!employees) {
            return;
        }

        const newSummaries = Coalesce.array(
            await dataManagerFactory.getConversationDataManager().getConversationSummaries(
                this._configuration.masterAccountId,
                EntityTypes.Employee,
                employees.map(e => e.employeeSourceId)) || []);

        newSummaries?.forEach(c => this._conversationSummariesDtoMap.set(c.entityId, c));

        employees.map(employee => {
            const conversationSummaryDto = this._conversationSummariesDtoMap.get(
                employee.employeeSourceId) ?? null;

            this._employeesMap.set(
                employee.employeeSourceId,
                new Employee(
                    employee,
                    employee.annualStandardWorkHours ?? 2080,
                    conversationSummaryDto,
                    this._configuration,
                    this._decisionDispatcher,
                    this.countryBudgetDecisionDispatcher));
        });
    }

    @action
    setBudgetBarUsage(isUsingBudgetBar: boolean): void {
        this.isUsingBudgetBar = isUsingBudgetBar;
    }

    @action
    async selectEmployee(employeeSourceId: string): Promise<void> {
        this.selectedEmployeeSourceId = employeeSourceId;
        const employee = this._employeesMap.get(employeeSourceId);
        if (!employee) {
            return;
        }
        await Promise.all([
            employee.getEmployeeInfo(this._configuration.masterAccountId),
            employee.initializePromotionRequest(),
            employee.loadPayHistory()
        ]);
    }

    @action
    deselectEmployee(): void {
        this.selectedEmployeeSourceId = null;
    }

    @action
    selectConversation(entityId: string): void {
        this.selectedConversationEntityId = entityId;
    }

    @action
    deselectConversation(): void {
        this.selectedConversationEntityId = null;
    }

    @action
    filterOnEmployee(employeeId: string): boolean {
        const employee = this.getEmployee(employeeId);
        this.searchedEmployee = employee;
        return !!employee;
    }

    @action
    removeFilterOnEmployee(): void {
        this.searchedEmployee = null;
    }

    @action
    setFilterByJob(jobTitle: string): void {
        this.filterByJob = jobTitle;
    }

    @action
    setFilterByDepartment(department: string): void {
        this.filterByDepartment = department;
    }

    @action
    setJobAndDepartmentFilter(jobFilter: string, departmentFilter: string): void {
        this.filterByJob = jobFilter;
        this.filterByDepartment = departmentFilter;
    }

    @action
    clearFilterByJob(): void {
        this.filterByJob = '';
    }

    @action
    clearFilterByDepartment(): void {
        this.filterByDepartment = '';
    }

    @action
    async setSortOptionAndUpdateFlag(
        isSortFormatUpdated: boolean,
        employeeSortFlag: EmployeeSort,
        isDirectsOnly: boolean): Promise<void> {
        this.isSortFormatUpdated = isSortFormatUpdated;
        this.employeeSort = employeeSortFlag;

        if (this.employeesNeedFetched && !this.isSorting) {
            // if we're calling the server for sorting then lets sneak one more page onto the front end
            // if we haven't already retrieved the data. Once the user gets all the data to the front end
            // then we can sort without calling the server
            this.page ++;
            this._maxViewableEmployees += this._numberEmployeesPerPage;

            this.isSorting = true;
            await this.getPagedEmployees(isDirectsOnly);
            this.isSorting = false;
        }
    }

    @action
    async setShowPromotionsOnly(promotionOnly: boolean, isDirectsOnly: boolean): Promise<void> {
        this.showPromotionsOnly = promotionOnly;
        const missingEmployees = this.employeesCount < this.count
            && this.employeesNeedFetched
            && !this.showPromotionsOnly;
        if (this.currentMaxViewableEmployees < this.count || missingEmployees) {
            await this.getPagedEmployees(isDirectsOnly);
        }
    }

    @computed
    get employeesNeedFetched(): boolean {
        return this.currentMaxViewableEmployees < this.count;
    }

    getEmployee(employeeSourceId: string): Nullable<Employee> {
        const employee = this._employeesMap.get(employeeSourceId);
        if ( employee) {
            return employee;
        }
        return null;
    }

    getBudget(budgetCycleId: Nullable<number>): Nullable<ICountryBudget> {
        if (!budgetCycleId) {
            return null;
        }
        const budget = this._budgetsMap.get(budgetCycleId);
        if ( budget) {
            return budget;
        }
        return null;
    }

    get budgetsMap(): Map<number, string> {
        return this._budgetNamesMap;
    }

    @computed
    get currentMaxViewableEmployees(): number {
        return this._maxViewableEmployees;
    }

    // For testing purposes only
    setMaxEmployees(count: number): void {
        this._maxViewableEmployees = count;
    }

    async setEmployees(employees: IEmployee[]): Promise<void> {
        this._employeesMap.clear();
        await this.addPagedEmployeesToMap(employees);
    }

    private employeeNameForSort(employee: Employee): string {
        return this.nameFormatMap[this._configuration.employeeNameSortFormat](employee);
    }

    private async getPagedEmployees(isDirectsOnly: boolean): Promise<void> {
        const dataManager = dataManagerFactory.getManagerDataManager();
        const employees = await dataManager.getPagedEmployees(
            this._configuration.masterAccountId,
            this._configuration.cycleId,
            managerListSidePanelStore.selectedManagerIds,
            isDirectsOnly,
            this.employeeSort,
            this._numberEmployeesPerPage,
            0, // sorting should always start with the first page
            this._configuration.teamPageViews.multiCountry ? this.code : '',
            this.page + 1,
            this.showPromotionsOnly
        );
        this.setEmployees(employees ?? []);
    }
}